import { getFirestore, doc, getDoc } from "firebase/firestore";
import { app } from "../firebase-config.js";
import { WebNovelViewer } from "../components/feature/viewer/WebNovelViewer.js";
import { EpisodeAdapter } from "../services/episode-adapter.service.js";
import { ContentService } from "../services/content.service.js";
import { PointService } from "../services/point.service.js";
import { PurchaseModal } from "../components/modals/PurchaseModal.js";
// Common Modules
import { ViewerHeader } from "../components/feature/viewer/ViewerHeader.js";
import { ViewerFooter } from "../components/feature/viewer/ViewerFooter.js";
import { ViewerController } from "../components/feature/viewer/ViewerController.js";
import { ViewerSettingsPanel } from "../components/feature/viewer/ViewerSettingsPanel.js";
import { ViewerSettingsService } from "../services/viewer-settings.service.js";
import { ViewerResumeEngine } from "../components/feature/viewer/ViewerResumeEngine.js";
import { CopyrightWarning } from "../components/feature/viewer/CopyrightWarning.js";
// Feature Modules
import { CommentSection } from "../components/feature/comment/CommentSection.js";
import { VerificationService } from "../services/verification.service.js";

const db = getFirestore(app);

export const WebnovelViewerPage = async (seriesId, episodeId, options = {}) => {
    const { isEmbedded = false, previewData = null } = options;
    const uid = window.user ? window.user.uid : null;

    // 1. Security Check
    if (!isEmbedded && !previewData && !window.user) {
        window.Modal.toast("로그인이 필요한 서비스입니다.");
        window.router.navigate('/login');
        return document.createElement('div');
    }

    if (!isEmbedded && !previewData && window.user) {
        // [PHASE 4] Age Guard
        // Note: Series Data isn't loaded yet. We need it. 
        // Logic moved to after data fetch or we assume we fetch it here.
    }

    window.scrollTo(0, 0);

    const epIndex = parseInt(episodeId) || 0;
    let seriesData = null;
    let epData = null;
    let hasNext = false;
    let hasPrev = false;
    let nextIndex = -1;
    let prevIndex = -1;
    let allEpisodes = [];

    // 2. Data Fetching
    if (previewData) {
        seriesData = { title: previewData.seriesTitle || "Preview Series" };
        epData = previewData;
    } else {
        try {
            const seriesRef = doc(db, "series", seriesId);
            const seriesSnap = await getDoc(seriesRef);
            if (seriesSnap.exists()) {
                seriesData = seriesSnap.data();

                // [PHASE 4] Age Guard Implementation
                const ageLimit = seriesData.ageLimit !== undefined ? seriesData.ageLimit : (seriesData.isAdult ? 19 : 0);
                const isAdmin = window.user && ['admin', 'super_admin'].includes(window.user.role);

                if (ageLimit >= 19 && !isAdmin) {
                    // Check if verified
                    // Note: window.user might be stale, but we rely on it for synchronous check.
                    // If critical, we should fetch UserService.getUserProfile(uid).
                    const isVerified = window.user.isAdultVerified === true;

                    if (!isVerified) {
                        const confirmVerify = await window.Modal.confirm(
                            "이 작품은 만 19세 이상 관람가입니다.\n성인 인증을 진행하시겠습니까?",
                            "성인 연령 확인"
                        );

                        if (confirmVerify) {
                            // Determine return URL
                            const returnUrl = window.location.origin + `/series/${seriesId}/episode/${episodeId}`;

                            // Load Settings for Key
                            // We need to fetch global settings to pass keys? 
                            // VerificationService.requestAdultVerification handles fetching or we pass it?
                            // The service signature is `requestAdultVerification: async (returnUrl, settings = {})`
                            // We should probably fetch settings or let service handle default.
                            // But service expects settings if we want dynamic keys.
                            const globalSettings = await ContentService.getGlobalSettings();
                            await VerificationService.requestAdultVerification(returnUrl, globalSettings);
                            return document.createElement('div'); // Pause rendering
                        } else {
                            window.router.navigate(`/series/${seriesId}`);
                            return document.createElement('div');
                        }
                    }
                }

                // [REFACTOR] Standardized Episode Logic via Adapter
                const subEpisodes = await ContentService.getEpisodes(seriesId);
                allEpisodes = EpisodeAdapter.adapt(seriesData, subEpisodes);

                // Robust Episode Lookup (ID or Index)
                const targetId = String(episodeId);
                const currentEp = allEpisodes.find(ep => String(ep.originalId) === targetId || String(ep.index - 1) === targetId)
                    || allEpisodes[parseInt(episodeId) || 0];

                if (currentEp) {
                    epData = currentEp;

                    // Security: Filter Drafts
                    const isAuthor = window.user && window.user.uid === seriesData.authorId;
                    const readableEpisodes = EpisodeAdapter.filterForReader(allEpisodes, isAuthor);

                    // Navigation Calculation
                    const currentIndexInList = readableEpisodes.findIndex(ep => ep.index === currentEp.index);
                    const prevEp = readableEpisodes[currentIndexInList - 1];
                    const nextEp = readableEpisodes[currentIndexInList + 1];

                    if (prevEp) {
                        hasPrev = true;
                        // [CRITICAL] 0-based index for URL
                        prevIndex = prevEp.index - 1;
                    }

                    if (nextEp) {
                        hasNext = true;
                        nextIndex = nextEp.index - 1;
                    }
                }

                // Analytics
                import('../services/analytics.service.js').then(({ AnalyticsService }) => {
                    AnalyticsService.logView(seriesId, seriesData.authorId);
                    AnalyticsService.logHourlyView(seriesId);
                });

                // Rewards
                if (window.user) {
                    import('../services/point.service.js').then(({ PointService }) => {
                        PointService.earnReadingReward(seriesId, episodeId).catch(e => { });
                    });
                }
            }
        } catch (e) {
            console.error(e);
            return document.createTextNode("Error loading novel.");
        }
    }

    if (!epData) return document.createTextNode("Episode not found.");

    // [SEO & AI Metadata Injection]
    // Fetch AI Analysis (SEO/GEO) to enrich the page metadata
    try {
        if (!seriesId || !episodeId) throw new Error("Invalid Series/Episode ID for SEO Fetch");

        console.log(`[Webnovel] Fetching SEO Metadata for ${seriesId}/${episodeId}`);
        const aiMetaRef = doc(db, "series", seriesId, "episodes", episodeId, "ai_metadata", "seo_analysis");
        const aiMetaSnap = await getDoc(aiMetaRef);
        const aiData = aiMetaSnap.exists() ? aiMetaSnap.data() : null;

        // Dynamic Imports for SEO
        const { SEOManager } = await import('../utils/seo-manager.js');
        const { JsonLdInjector } = await import('../components/feature/seo/JsonLdInjector.js');

        // 1. Basic Meta Tags
        SEOManager.updateMeta({
            title: `${seriesData?.title || 'Webnovel'} - ${epData.title}`,
            description: aiData?.hook || epData.note || `${seriesData?.title}의 ${epData.title} 에피소드입니다.`,
            image: seriesData?.thumbnail || '/og.webp',
            keywords: aiData?.keywords ? aiData.keywords.join(',') : 'webnovel,fiction,story'
        });

        // 2. Advanced JSON-LD (Schema.org) with GEO Data
        // Convert 'Real' locations to identifiable content
        const locationContext = aiData?.locations?.real ? aiData.locations.real : [];

        JsonLdInjector.inject({
            mode: 'article', // or 'chapter'
            title: epData.title,
            description: aiData?.hook || epData.note || "Read this episode on Ssulwha.",
            author: seriesData?.authorId || "Unknown", // Ideally resolve name
            datePublished: epData.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            image: seriesData?.thumbnail,
            // [AI GEO Injection]
            ocrContext: {
                locations: locationContext,
                vibes: aiData?.keywords || []
            }
        });

        console.log("[Webnovel] SEO Injected:", { hasAI: !!aiData, locations: locationContext });

    } catch (e) {
        console.warn("[Webnovel] SEO Injection Failed (Non-critical):", e);
    }

    // [SECURITY] Draft Guard (Current Episode)
    const isAuthor = window.user && window.user.uid === seriesData.authorId;
    const isDraft = epData.status === 'draft';

    if (isDraft && !isAuthor) {
        window.Modal.alert("작성 중인 에피소드입니다.");
        window.router.navigate(`/series/${seriesId}`);
        return document.createElement('div');
    }

    // [PHASE 2] Initial Load Payment Gate (Webnovel)
    // [STRICT v1.36.191] Default Locked Policy
    // Logic: Limit 3 -> 1,2,3 Free. 4 Paid. (Index <= Limit)

    // 1. Fetch Global Policy
    const _globalSettings = await ContentService.getGlobalSettings();
    const _freeLimit = seriesData.freeLimit !== undefined ? seriesData.freeLimit : (_globalSettings.default_free_limit || 3);
    const _safeLimit = Number(_freeLimit);

    // 2. Check Free Policy (Standard 1-based Index)
    // EpisodeAdapter guarantees 'index' property (1, 2, 3...)
    // Current Episode = epData
    let _isFreeInit = false; // Default Locked
    if (epData.index <= _safeLimit) {
        _isFreeInit = true; // Exception
    }

    if (!_isFreeInit) {
        console.log("[Webnovel] Default Locked (Init):", { index: epData.index, limit: _safeLimit });

        if (!window.user) {
            window.Modal.toast("로그인이 필요한 유료 에피소드입니다.");
            window.router.navigate('/login');
            return document.createElement('div');
        }

        const _isUnlocked = await ContentService.checkEpisodeUnlock(window.user.uid, seriesId, episodeId);

        if (!_isUnlocked) {
            console.log("[Webnovel] Content Locked. Triggering Modal.");
            // Pricing Strategy
            const _SURCHARGE = seriesData.isAdult ? 50 : 0;
            const _RENT_DAYS = _globalSettings.rent_days || 7;
            const _typePolicies = _globalSettings.prices || {};
            const _policy = _typePolicies[seriesData.type] || _typePolicies['webnovel'] || { rent_price: 3, own_price: 5 };

            const _baseRent = epData.price || _policy.rent_price || 3;
            const _baseOwn = _policy.own_price || (_baseRent * 2);

            const _finalRent = _baseRent + _SURCHARGE;
            const _finalOwn = _baseOwn + _SURCHARGE;

            const _blocker = document.createElement('div');
            _blocker.className = "w-full h-screen flex flex-col items-center justify-center bg-zinc-900 text-white";
            _blocker.innerHTML = `<div class="text-center p-8"><h2 class="text-2xl font-bold mb-4">🔒 유료 에피소드</h2><p>구매 후 열람할 수 있습니다.</p>` +
                `<button onclick="window.history.back()" class="mt-4 px-4 py-2 border border-zinc-700 rounded hover:bg-zinc-800 text-sm">뒤로 가기</button>` +
                `</div>`;

            new PurchaseModal({
                title: "잠금 해제",
                itemName: `${seriesData.title} - ${epData.title}`,
                rentPrice: _finalRent,
                rentDays: _RENT_DAYS,
                ownPrice: _finalOwn,
                isAdult: seriesData.isAdult,
                onConfirm: async (type) => {
                    try {
                        const price = type === 'rent' ? _finalRent : _finalOwn;
                        await PointService.unlockContent(seriesId, episodeId, type, price, `${seriesData.title} - ${epData.title}`);
                        window.Modal.toast("결제가 완료되었습니다.");
                        window.location.reload();
                    } catch (e) { console.error(e); }
                },
                onCancel: () => { window.router.navigate(`/series/${seriesId}`); }
            }).render();
            return _blocker;
        }
    }

    // ... imports ...

    // 3. Setup Layout
    const container = document.createElement('div');
    // Initial class (Setting Panel will override)
    container.className = "w-full min-h-screen relative transition-colors duration-300 viewer-theme-root theme-dark";

    // Render Core Viewer
    // [FIX] Initialize Viewer and Capture API
    const viewerApi = await WebNovelViewer(container, {
        content: epData.content,
        title: epData.title,
        note: epData.note,
        hasPrev,
        hasNext,
        onPrev: () => navCallbacks.onPrev(),
        onNext: () => navCallbacks.onNext(),
        isbn: seriesData.isbn,
        uci: seriesData.uci
    });

    // Host Alias for legacy slots
    const host = container;

    // B. Navigation Handlers
    const navCallbacks = {
        onNext: async () => {
            if (hasNext && nextIndex !== -1) {
                // [PHASE 1] Secure Payment Gate
                const targetEp = allEpisodes[nextIndex];
                if (!targetEp) {
                    window.Modal.toast("에피소드 정보를 찾을 수 없습니다.");
                    return;
                }

                // 1. Fetch Global Policy & Determine Free Limit (Robust)
                const globalSettings = await ContentService.getGlobalSettings();

                // [FIX v1.36.192] Robust Free Limit (Null & SnakeCase Guard)
                const rawLimit = seriesData.freeLimit ?? seriesData.free_limit ?? globalSettings.default_free_limit ?? 3;
                const freeLimit = Number(rawLimit);
                const safeFreeLimit = isNaN(freeLimit) ? 3 : freeLimit;

                // 2. Check Free Policy (Strict)
                // [STRICT v1.36.191] Default Locked Policy.

                let isFree = false; // Default Locked
                if (targetEp.index <= safeFreeLimit) {
                    isFree = true;
                }

                if (isFree) {
                    window.router.navigate(`/series/${seriesId}/episode/${nextIndex}`);
                    return;
                }

                // 3. User & Auth Check
                if (!window.user) {
                    if (confirm("로그인이 필요합니다. 로그인하시겠습니까?")) window.router.navigate('/login');
                    return;
                }

                // 4. Check Ownership (Unlock Status)
                const isUnlocked = await ContentService.checkEpisodeUnlock(window.user.uid, seriesId, targetEp.id);
                if (isUnlocked) {
                    window.router.navigate(`/series/${seriesId}/episode/${nextIndex}`);
                    return;
                }

                // 5. Dynamic Pricing & Adult Surcharge
                const SURCHARGE = seriesData.isAdult ? 50 : 0;
                const RENT_DAYS = globalSettings.rent_days || 7; // [RENTAL] Dynamic Days

                // Policy Fallback
                const typePolicies = globalSettings.prices || {};
                const policy = typePolicies[seriesData.type] || typePolicies['webnovel'] || { rent_price: 50, own_price: 100 };

                // Calculate Final Prices
                // [FIX v1.36.192] Type Safety & Zero Guard
                let baseRent = Number(targetEp.price || policy.rent_price || 50);
                // Own price: if not set in episode, fallback to policy or 2x rent
                let baseOwn = Number(policy.own_price || (baseRent * 2));

                if (baseRent <= 0 || isNaN(baseRent)) {
                    console.warn("[Payment] Invalid/Zero Rent Price Detected. Forcing Default 50P.", baseRent);
                    baseRent = 50;
                }
                if (baseOwn <= 0 || isNaN(baseOwn)) {
                    baseOwn = 100;
                }

                const finalRent = baseRent + SURCHARGE;
                const finalOwn = baseOwn + SURCHARGE;

                // 6. Show Gatekeeper Modal
                new PurchaseModal({
                    title: "다음 화 잠금 해제",
                    itemName: `${seriesData.title} - ${targetEp.title}`,
                    rentPrice: finalRent,
                    rentDays: RENT_DAYS, // [RENTAL] Pass dynamic days
                    ownPrice: finalOwn,
                    isAdult: seriesData.isAdult,
                    onConfirm: async (type) => {
                        try {
                            const price = type === 'rent' ? finalRent : finalOwn;
                            // [SECURE] Server-Side Verify & Grant (Rent/Own Check)
                            await PointService.unlockContent(seriesId, targetEp.id, type, price, `${seriesData.title} - ${targetEp.title}`);

                            window.Modal.toast("결제가 완료되었습니다.");
                            // Navigate
                            window.router.navigate(`/series/${seriesId}/episode/${nextIndex}`);
                        } catch (e) {
                            console.error(e);
                            // Error is handled inside Modal usually, but this is double safety
                            throw e; // Modal catches this to reset button
                        }
                    }
                }).render();

            } else {
                // End of Series
                window.Modal.toast("마지막 에피소드입니다.");
            }
        },
        onPrev: () => {
            if (hasPrev && prevIndex !== -1) window.router.navigate(`/series/${seriesId}/episode/${prevIndex}`);
        }
    };

    if (!isEmbedded) {
        // C. Settings (Theme & Font)
        // Ensure we load settings and apply them IMMEDIATELY to avoid FOUC
        const settingsPanel = new ViewerSettingsPanel({
            uid,
            onSettingsChange: ({ theme, fontSize }) => {
                // Apply Theme to Container
                if (theme) {
                    container.className = `font-sans w-full min-h-screen flex flex-col relative transition-colors duration-300 viewer-theme-root ${theme}`;
                    const styles = ViewerSettingsService.getThemeStyles(theme.replace('theme-', ''));
                    if (styles) {
                        container.style.backgroundColor = styles.bg;
                        container.style.color = styles.text;
                        container.style.setProperty('--bg', styles.bg);
                        container.style.setProperty('--text', styles.text);
                    }
                }
                // Apply Font Size to Viewer API
                if (fontSize && viewerApi) {
                    viewerApi.setFontSize(fontSize);
                }
            }
        });

        // D. Header (Fixed)
        const header = new ViewerHeader(document.body, { // Mount to body for fixed z-index
            title: seriesData?.title || 'Webnovel',
            viewerType: 'novel',
            onBack: () => window.router.navigate(`/series/${seriesId}`),
            onSettings: () => settingsPanel.toggle(), // Connect Settings Button
            onSearch: () => import('../components/search-modal.js').then(({ SearchModal }) => SearchModal.show())
        });

        // Ensure Header cleans up when this page is destroyed
        // We can attach a destroy method to the container for the router
        container.destroy = () => {
            header.destroy();
            settingsPanel.destroy();
            controller.destroy();
            ViewerResumeEngine.destroy(window);
        };

        // E. Footer (Engagement Hub)
        // Replaces the internal slot usage if we want, OR we mount it to the body as fixed/static? 
        // StandardFooter is designed to be "End of Content" in most cases, or Fixed?
        // Plan says "Viewer Footer... 연동". The ViewerFooter.js I wrote is a static div (not fixed position).
        // So we should append it to the END of the content.
        // WebNovelViewer has `#viewer-footer` slot. Perfect.
        const footerSlot = host.querySelector('#viewer-footer');
        if (footerSlot) {
            const footer = new ViewerFooter(footerSlot, {
                seriesId,
                episodeId,
                seriesData,
                hasPrev,
                hasNext,
                onPrev: navCallbacks.onPrev,
                onNext: navCallbacks.onNext
            });

            // F. Rating (Into Footer Slot)
            const ratingSlot = footerSlot.querySelector('#vf-rating-slot');
            if (ratingSlot) {
                import('../components/feature/viewer/RatingComponent.js').then(({ RatingComponent }) => {
                    RatingComponent(ratingSlot, {
                        seriesId,
                        episodeId,
                        initialRating: seriesData?.stats?.ratingAverage || 0,
                        initialCount: seriesData?.stats?.ratingCount || 0
                    });
                });
            }

            // G. Comments (Into Footer Slot)
            const commentSlot = footerSlot.querySelector('#vf-comment-slot');
            if (commentSlot) {
                CommentSection(commentSlot, {
                    seriesId,
                    episodeId,
                    currentUser: window.user,
                    authorId: seriesData?.authorId,
                    hasNext,
                    onNext: navCallbacks.onNext
                });
            }
        }

        // G. FAB Controller (Floating)
        // [FIX] Robust BGM Extraction for Legacy/New Compatibility
        const bgmSource = epData.bgm || epData.bgmUrl || epData.metadata?.bgm || null;

        // [New] Real-time Translation
        const { templateTranslationService } = await import('../services/TranslationService.js');
        let originalContent = { title: epData.title, content: epData.content };

        const controller = new ViewerController(document.body, {
            viewerType: 'novel',
            bgmUrl: bgmSource,
            content: epData.content,
            onScrollTop: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
            onTranslate: async (isTranslated) => {
                if (!isTranslated) {
                    // Revert to Original
                    viewerApi.updateContent(originalContent.content, originalContent.title);
                    window.Modal.toast("원본 언어로 복귀했습니다.");
                    return;
                }

                // Ask for Language
                const lang = await window.Modal.showLanguageSelector("en");
                if (!lang) {
                    // Canceled
                    controller.toggleTranslate(controller.wrapper.querySelector('#btn-translate')); // Toggle back visual state
                    return;
                }

                window.Modal.toast(`${lang.toUpperCase()} 번역 중... 잠시만 기다려주세요.`);
                try {
                    const [trTitle, trContent] = await Promise.all([
                        templateTranslationService.translate(originalContent.title, lang),
                        templateTranslationService.translate(originalContent.content, lang)
                    ]);

                    viewerApi.updateContent(trContent, trTitle);
                    window.Modal.toast("번역이 완료되었습니다.");
                } catch (e) {
                    console.error("Translation Failed", e);
                    window.Modal.alert(`번역 실패: ${e.message}`);
                    controller.toggleTranslate(controller.wrapper.querySelector('#btn-translate')); // Revert visual
                }
            }
        });

        // H. Resume Engine & Continuity
        ViewerResumeEngine.init(window, {
            seriesId,
            episodeId,
            title: seriesData?.title,
            thumbnail: seriesData?.thumbnail,
            seriesTitle: seriesData?.title
        }, { episodeIndex: epIndex });

        // Restore Check (>95% check included)
        await ViewerResumeEngine.restore(window, seriesId, episodeId);

        // I. Copyright Warning (Delayed for Effect)
        // I. Copyright Warning (Delayed for Effect)
        setTimeout(() => {
            // [FACTORY PATTERN] Unified Fetch
            import('../services/admin-popup.service.js').then(async ({ AdminPopupService }) => {
                try {
                    // [Refactor] Use Centralized Resolution (Auto-Language Support)
                    const finalConfig = await AdminPopupService.getResolvedCopyrightSettings();

                    // Fallback for DaVinci/CineNovel Defaults if no Custom Mode
                    if (finalConfig.mode !== 'custom' && seriesData.type === 'davinci') {
                        Object.assign(finalConfig, {
                            title: "동작 그만! 캡처냐?",
                            icon: "👮‍♂️",
                            buttonText: "아닙니다! 눈으로만 보겠습니다!",
                            theme: 'police'
                        });
                    }

                    if (finalConfig.mode === 'custom') {
                        finalConfig.icon = finalConfig.theme === 'police' ? '👮‍♂️' : (finalConfig.theme === 'horror' ? '👻' : '⚠️');
                    }

                    CopyrightWarning(container, finalConfig);

                } catch (e) {
                    console.warn("Copyright Settings Fetch Error", e);
                    CopyrightWarning(container); // Safe Fallback
                }
            });
        }, 100); // [FIX] Reduced delay to 100ms per Emergency Plan
    }

    return container;
};
